// material ui
import { Button, Dialog, Grid, Typography } from '@material-ui/core';
// material table
import MaterialTable, { Column, EditComponentProps } from 'material-table';
import RowData from 'material-table';
import React, { ReactElement } from 'react';

// api
import { IPublisher } from '~/src/dash/api/console-api';
import {
  ENetworkRole,
  INetworkPublisherMap,
  networkRoleNumberToRole,
  networkRoleToString,
} from '~/src/dash/api/network';
// other
import { IWithStylesProps } from '~/src/dash/types/props';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { networkRoleToChip } from '~/src/dash/utils/network-status';

// COMPONENT DESCRIPTION: PublisherRolesTable is used in the NetworkSettingsView for the Roles tab.

interface IProps extends IWithStylesProps {
  // map of ids to publishers
  publisherMap: IPublisherMap;
  // network publisher map info from parent
  networkPublisherMaps: INetworkPublisherMap[];
  // handlers
  handleSnackbarError: (snackbarError: boolean, snackbarMessage: string) => void;
  handleAddNetworkPublisherMap: (role: INetworkPublisherRowData) => void;
  handleUpdateNetworkPublisherMap: (role: INetworkPublisherRowData, index: number) => void;
  handleDeleteNetworkPublisherMap: (index: number) => void;
}

interface IState {
  // identifies which row of the table is actively being edited
  selectedPublisherID: string;
  // identifies the index of the row to pass to handlers
  selectedNetworkPublisherMapIndex: number | undefined;
  // open/close model
  openPublisherSelectModel: boolean;
  // columns defined for material-table
  columns: Array<Column<INetworkPublisherRowData>>;
}

export interface INetworkPublisherRowData {
  PublisherID: number;
  NetworkPublisherRole: ENetworkRole;
}

export interface IPublisherMap {
  [key: string]: IPublisher;
}

// additional column props can be found at https://material-table.com/#/docs/get-started
const tableColumnsPublisher = [
  { title: 'ID', field: 'ID' },
  { title: 'Name', field: 'Name' },
  { title: 'Domain', field: 'Domain', searchable: false },
  { title: 'Region', field: 'DefaultRegion', searchable: false },
];

export const PublisherRolesTable = decorate(
  {
    // NOTE: Use styles when the CSS is used more than once
    styles: () => ({
      normalText: {
        fontWeight: 400,
        fontSize: 14,
      },
      boldText: {
        fontWeight: 500,
        fontSize: 14,
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    // Constructor
    constructor(props: IProps) {
      super(props);
      this.state = {
        selectedPublisherID: '',
        selectedNetworkPublisherMapIndex: undefined,
        openPublisherSelectModel: false,
        columns: [
          {
            title: 'Publisher',
            field: 'PublisherID',
            cellStyle: {
              textAlign: 'center',
            },
            headerStyle: {
              textAlign: 'center',
            },
            render: (rowData: INetworkPublisherRowData) => {
              const pub = this.props.publisherMap[rowData.PublisherID];
              if (pub === undefined) {
                return <Typography></Typography>;
              }
              return (
                <Typography className={this.props.classes.normalText}>
                  [{pub.ID.toString()}] {pub.Name}
                </Typography>
              );
            },
            // @ts-ignore
            editComponent: (props: EditComponentProps<RowData>) => {
              // if the value (publisher ID) is not blank
              if ((props.value || '') !== '') {
                const pub = this.props.publisherMap[props.value];
                return (
                  <Button
                    onClick={() => {
                      props.onChange(undefined);
                      this.setState({
                        openPublisherSelectModel: true,
                        selectedPublisherID: '',
                        selectedNetworkPublisherMapIndex: props.columnDef.tableData.id,
                      });
                    }}
                  >
                    <Typography className={this.props.classes.boldText}>
                      [{pub.ID.toString()}] {pub.Name}
                    </Typography>
                  </Button>
                );
              } else if (this.state.selectedPublisherID === '') {
                // if the publisher ID is blank
                return (
                  <Button
                    onClick={() => {
                      this.setState({
                        openPublisherSelectModel: true,
                        selectedNetworkPublisherMapIndex: props.columnDef.tableData.id,
                      });
                    }}
                  >
                    <Typography className={this.props.classes.normalText}>
                      Select a Publisher
                    </Typography>
                  </Button>
                );
              } else {
                // changes the selected publisher for that row
                props.onChange(parseInt(this.state.selectedPublisherID, 10));
                this.setState({
                  selectedPublisherID: '',
                });
                return <Typography />;
              }
            },
          },
          {
            title: 'Role',
            field: 'NetworkPublisherRole',
            cellStyle: {
              textAlign: 'center',
            },
            headerStyle: {
              textAlign: 'center',
            },
            lookup: {
              1: networkRoleToString(ENetworkRole.ROLE_OWNER),
              2: networkRoleToString(ENetworkRole.ROLE_READ),
            },
            render: (rowData: INetworkPublisherRowData) => {
              return this.renderRoleChip(rowData.NetworkPublisherRole);
            },
          },
        ],
      };
    }

    // -------- ROLES HELPER FUNCTIONS -------- //

    // validates the publisher and role when updating a network publisher map
    validateNetworkPublisherMap(
      networkPublisherMap: INetworkPublisherRowData,
      reject: (reason?: any) => void,
    ): boolean {
      if (!networkPublisherMap.PublisherID) {
        this.props.handleSnackbarError(true, 'You must select a publisher');
        reject();
        return false;
      }

      if (
        isNaN(networkPublisherMap.NetworkPublisherRole) ||
        networkPublisherMap.NetworkPublisherRole === ENetworkRole.ROLE_UNKNOWN
      ) {
        this.props.handleSnackbarError(true, 'You must set a role');
        reject();
        return false;
      }

      return true;
    }

    handleRowClick = (publisher: IPublisher): void => {
      this.setState({
        openPublisherSelectModel: false,
        selectedPublisherID: publisher.ID,
      });
    };

    // -------- RENDER HELPER FUNCTIONS -------- //

    renderRoleChip = (role: number): ReactElement => {
      const ChipComponent = networkRoleToChip(networkRoleNumberToRole(role));
      if (ChipComponent === undefined) {
        return <Typography>Unknown</Typography>;
      }
      return <ChipComponent label={networkRoleToString(role)} />;
    };

    // filters out publishers that are already selected by other network publisher maps
    renderPublisherListModel = (): ReactElement => {
      const { publisherMap, networkPublisherMaps } = this.props;
      const { openPublisherSelectModel } = this.state;
      const validPublishers = Object.values(publisherMap).filter((publisher: IPublisher) => {
        return (
          networkPublisherMaps.find((a: INetworkPublisherRowData) => {
            return a.PublisherID.toString() === publisher.ID;
          }) === undefined
        );
      });

      return (
        <Dialog
          fullWidth
          maxWidth='md'
          keepMounted
          open={openPublisherSelectModel}
          onClose={() =>
            this.setState({
              openPublisherSelectModel: false,
              selectedPublisherID: '',
            })
          }
        >
          <MaterialTable
            style={{
              paddingLeft: 20,
              paddingRight: 20,
            }}
            title='Select a Publisher'
            options={{
              pageSize: 15,
              pageSizeOptions: [5, 15, 30],
            }}
            columns={tableColumnsPublisher}
            data={validPublishers}
            onRowClick={(_: undefined, publisher: IPublisher) => this.handleRowClick(publisher)}
          />
        </Dialog>
      );
    };

    render() {
      const {
        networkPublisherMaps,
        handleAddNetworkPublisherMap,
        handleDeleteNetworkPublisherMap,
        handleUpdateNetworkPublisherMap,
      } = this.props;
      const { columns } = this.state;
      return (
        <Grid style={{ width: '100%' }}>
          <MaterialTable
            style={{
              paddingLeft: '15px',
            }}
            title='Roles *'
            options={{
              search: false,
              sorting: false,
              padding: 'dense',
              pageSize: 5,
              pageSizeOptions: [3, 5, 10],
            }}
            columns={columns}
            data={networkPublisherMaps}
            editable={{
              onRowAdd: (networkPublisherMap: INetworkPublisherRowData) => {
                // material table switches to string when using "lookup" dropdown so switch role back to number
                networkPublisherMap.NetworkPublisherRole = networkPublisherMap.NetworkPublisherRole
                  ? parseInt(networkPublisherMap.NetworkPublisherRole.toString(), 10)
                  : ENetworkRole.ROLE_OWNER;
                this.setState({
                  selectedPublisherID: '',
                });
                return new Promise((resolve, reject) => {
                  if (!this.validateNetworkPublisherMap(networkPublisherMap, reject)) {
                    return;
                  }
                  handleAddNetworkPublisherMap(networkPublisherMap);
                  resolve(undefined);
                });
              },
              onRowUpdate: (
                networkPublisherMap: INetworkPublisherRowData,
                oldData: INetworkPublisherRowData,
              ) => {
                // material table switches to string when using "lookup" dropdown so switch role back to number
                networkPublisherMap.NetworkPublisherRole = networkPublisherMap.NetworkPublisherRole
                  ? parseInt(networkPublisherMap.NetworkPublisherRole.toString(), 10)
                  : ENetworkRole.ROLE_OWNER;
                const index = networkPublisherMaps.indexOf(oldData as never);
                this.setState({
                  selectedPublisherID: '',
                });
                return new Promise((resolve, reject) => {
                  if (!this.validateNetworkPublisherMap(networkPublisherMap, reject)) {
                    return;
                  }
                  handleUpdateNetworkPublisherMap(networkPublisherMap, index);
                  resolve(undefined);
                });
              },
              onRowDelete: (oldData: INetworkPublisherRowData) => {
                const index = networkPublisherMaps.indexOf(oldData as never);
                this.setState({
                  selectedPublisherID: '',
                });
                return new Promise((resolve) => {
                  handleDeleteNetworkPublisherMap(index);
                  resolve(undefined);
                });
              },
            }}
          />
          {this.renderPublisherListModel()}
        </Grid>
      );
    }
  },
);
